import { router, useLocalSearchParams } from 'expo-router';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useState } from 'react';

import { useValidateOtpV2Mutation } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';

import { useAuthRouteBase } from '../auth-route-base-context';
import { cognitoSignIn } from '../cognito';
import { resolveOAuthStateForMutation, tryOAuthRedirectAfterLogin } from '../oauth';
import { AuthV2ChallengeType, IdentifyAuthJwt } from '../types';

import { useAuthNavigationParams } from './use-auth-navigation-params';

import { useAuthContext, useCRMEventsContext, useToast } from '.';

export const useValidateOtp = ({ onIdentifyChallenge }) => {
  const [identifyAuthJwts, setIdentifyAuthJwts] = useState<IdentifyAuthJwt[]>([]);
  const [validatingOtp, setValidatingOtp] = useState<boolean>(false);
  const [otpValidationError, setOtpValidationError] = useState<boolean>(false);
  const isDesktop = useIsDesktopBp();
  const { loginJwt } = useLocalSearchParams<{ loginJwt?: string }>();
  const { paths } = useAuthRouteBase();
  const authNavigationParams = useAuthNavigationParams();
  //When we are fully on auth V2 we should remove this state and just handle the authenticated state ourselves.
  const { refreshCurrentUserWithNewSession, originLocation } = useAuthContext();
  const { logLoginEvent, logValidateAttempt } = useCRMEventsContext();
  const onValidateOtp = useCallback(
    (data, _clientOptions) => {
      router.setParams({
        ...authNavigationParams,
        loginJwt: data.validateOtpV2.loginJwt,
      });
      return data;
    },
    [authNavigationParams]
  );
  const { show } = useToast();
  const onError = useCallback(
    err => {
      if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
        //Take the highest level error code
        const code = err.graphQLErrors[0].extensions.code;
        switch (code) {
          case 'INVALID_OTP':
            setOtpValidationError(true);
            break;

          case 'NO_VALID_CODES':
            router.dismissTo(paths.signIn);
            show({
              text: "You've exceeded the limit of attempts. Please request a new code and try again.",
              variant: 'informative',
            });
            break;
          case 'TOO_MANY_REQUESTS':
            show({
              text: 'You have exceeded the limit of attempts. Please try again later.',
              variant: 'informative',
            });
            break;
          default:
            router.dismissTo(paths.signIn);
            show({ text: 'Oops, something went wrong. Please try again', variant: 'informative' });
        }
      } else {
        router.dismissTo(paths.signIn);
        show({ text: 'Oops, something went wrong. Please try again', variant: 'informative' });
      }
    },
    [paths.signIn, show]
  );
  const [internalValidateOtp] = useValidateOtpV2Mutation({
    onCompleted: onValidateOtp,
    onError: onError,
  });
  const validateOtp = useCallback(
    async (otpInput: string) => {
      try {
        setValidatingOtp(true);
        if (loginJwt) {
          const oauthState = resolveOAuthStateForMutation();
          const response = await internalValidateOtp({
            variables: {
              input: {
                otpInput,
                loginJwt,
                ...(oauthState !== undefined ? { oauthState } : {}),
              },
            },
          });
          const validateResult = response.data?.validateOtpV2;
          logValidateAttempt({
            successful: !!validateResult,
            challengeType: validateResult?.challengeType || AuthV2ChallengeType.SIGN_IN_CHALLENGE,
            numAccounts:
              validateResult?.challenge?.authJwts?.length ??
              (validateResult?.challengeType === AuthV2ChallengeType.SIGN_UP_CHALLENGE ? 0 : 1),
          });
          if (validateResult) {
            switch (validateResult.challengeType) {
              case AuthV2ChallengeType.SIGN_IN_CHALLENGE: {
                const { challengeCode, cognitoId, sessionId } = validateResult.challenge;
                //Authenticate with Cognito.
                const session = await cognitoSignIn(cognitoId, sessionId, challengeCode);
                //Validate the user is authenticated here
                if (session) {
                  //Update Local storage session info and trigger downstream listeners
                  await refreshCurrentUserWithNewSession();
                  //Push event to Amplitude
                  logLoginEvent({ method: 'signIn' });
                  if (validateResult.challenge.authorizationCode) {
                    tryOAuthRedirectAfterLogin({
                      authorizationCode: validateResult.challenge.authorizationCode,
                      redirectUriSearchParam: authNavigationParams.redirect_uri,
                      stateSearchParam: authNavigationParams.state,
                    });
                    break;
                  }
                  if (originLocation === '/cart') {
                    // only added because of the flag per user; needs to wait for flag to update
                    await new Promise(resolve => setTimeout(resolve, 300));

                    router.dismissTo({ pathname: '/cart', params: { loginJwt: undefined } });
                    break;
                  }
                  if (originLocation === '/account') {
                    router.dismissTo({ pathname: '/account', params: { loginJwt: undefined } });
                    break;
                  }
                  if (originLocation === 'PYPESTREAM' || originLocation === '/support') {
                    // authContext needs time to update the cognitoId, to avoid a race condition, give a small wait time before navigating
                    await new Promise(resolve => setTimeout(resolve, 300));
                    router.dismissTo({ pathname: '/support', params: { loginJwt: undefined } });
                  } else {
                    //Route to home page & clear login state
                    router.dismissTo({ pathname: '/', params: { loginJwt: undefined } });
                  }
                }
                break;
              }
              case AuthV2ChallengeType.SIGN_UP_CHALLENGE: {
                router.replace({
                  pathname: paths.signUp,
                  params: {
                    ...authNavigationParams,
                    loginJwt: validateResult.loginJwt,
                  },
                });
                break;
              }
              case AuthV2ChallengeType.IDENTIFY_CHALLENGE: {
                //Render select account popup
                const { authJwts } = validateResult.challenge;
                setIdentifyAuthJwts(
                  authJwts?.map(jwt => {
                    return {
                      ...jwtDecode<IdentifyAuthJwt>(jwt),
                      jwt,
                    };
                  }) ?? []
                );
                onIdentifyChallenge();
                break;
              }
              case AuthV2ChallengeType.OAUTH_REDIRECT_CHALLENGE: {
                const { authorizationCode } = validateResult.challenge;
                if (authorizationCode) {
                  tryOAuthRedirectAfterLogin({
                    authorizationCode,
                    redirectUriSearchParam: authNavigationParams.redirect_uri,
                    stateSearchParam: authNavigationParams.state,
                  });
                }
                break;
              }
            }
          }
        }
      } catch (e) {
        //Pretty unlikely case, but if the signin fails hard we will move the user back to sign in and display a toast message.
        show({
          text: 'Oops, something went wrong. Please try again',
          variant: 'informative',
          duration: 5000,
        });
        router.dismissAll();
      }
      setValidatingOtp(false);
    },
    [
      authNavigationParams,
      internalValidateOtp,
      isDesktop,
      logLoginEvent,
      logValidateAttempt,
      loginJwt,
      onIdentifyChallenge,
      originLocation,
      paths.signIn,
      paths.signUp,
      refreshCurrentUserWithNewSession,
      show,
    ]
  );
  return {
    validatingOtp,
    otpValidationError,
    setOtpValidationError,
    validateOtp,
    identifyAuthJwts,
  };
};
